import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
} from '@mui/material';
import styled from '@emotion/styled';
import { COLORS } from './../../utils/colors';
import { ICommonClient } from '../../services/client.service';
import { Loader } from '../Loader/Loader';

export interface ICardItem {
  id: string;
  title: string;
  desc: string;
}

export type CardsProps = {
  isLoading?: boolean;
  list: Array<ICommonClient>;
  desc: string;
  label: string;
  height?: string;
  onItemClick: Function;
  showBottomLoader?: boolean;
  // eslint-disable-next-line
  onScroll?: (e: React.UIEvent<HTMLElement>) => void;
};

export const DashboardCard = (props: CardsProps) => {
  const { isLoading, list, label, desc, height, onItemClick, onScroll } = props;

  const scrollRef = React.useRef<HTMLDivElement>(null);

  return (
    <CardStyled height={height}>
      <StickyHeader>
        <Box p={2}>
          <Typography variant='h5'>{label}</Typography>
          <Typography variant='caption' color='grey'>
            {desc}
          </Typography>
        </Box>
      </StickyHeader>

      <ScrollableContent ref={scrollRef} onScroll={onScroll} height={height}>
        {isLoading ? (
          <Loader loaderBoxStyle={{ height: '0vh' }} />
        ) : list?.length > 0 ? (
          <List disablePadding>
            {list.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton onClick={() => onItemClick(item)}>
                  <ListItemText
                    primary={`${item.firstName} ${item.lastName}`}
                    primaryTypographyProps={{ fontWeight: '500', color: COLORS.palette.blue }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant='body2' textAlign={'center'} sx={{ m: 0, pt: 5 }} gutterBottom>
            No result
          </Typography>
        )}
        {/* Bottom loader only for paginated loading */}
        {!isLoading && props.showBottomLoader && (
          <Box textAlign='center' py={2}>
            <CircularProgress size={20} />
          </Box>
        )}
      </ScrollableContent>
    </CardStyled>
  );
};

const StickyHeader = styled(Box)({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: COLORS.palette.white,
  borderBottom: `1px solid ${COLORS.palette.borderColor}`,
  borderRadius: '8px 8px 0 0',
});

// Remove scroll-related styles from CardStyled
const CardStyled = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'height',
})<{ height?: string }>(({ height }) => ({
  background: COLORS.palette.white,
  border: `1px solid ${COLORS.palette.borderColor}`,
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  height: height || '376px',
}));

const ScrollableContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'height',
})<{ height?: string }>(({ height }) => ({
  p: 0,
  m: 0,
  paddingTop: 1,
  height: height || '376px',
  overflowY: 'auto',
  flex: 1,
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,.1)',
    borderRadius: '8px',
  },
}));
